import React, { useState, useEffect } from 'react';
import { Save, Send, Mail } from 'lucide-react';
import { Button } from '../common/Button';
import { useUpdateDeliveryDetailsMutation, useSendDeliveryScheduleEmailMutation } from '../../store/slices/apiSlice';
import { useToast } from '../../hooks/useToast';
interface DeliveryDetailsFormProps {
  jobId: string;
  initialData: {
    delivery_window_start?: number | null;
    delivery_window_end?: number | null;
    delivery_status?: string | null;
    delivery_notes?: string | null;
  };
  potentialDeliveryDate?: string | null;
  deliveryScheduleEmailSentAt?: string | null;
  deliveryScheduleEmailSentByUsername?: string | null;
  onSave?: (data: any) => void;
  onStatusChange?: (status: string) => void;
  onEmailSent?: () => void;
}

const STATUS_OPTIONS = [
  { value: '', label: 'Select Status...' },
  { value: 'confirmed', label: 'Confirmed Booking' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'voicemail_email', label: 'Voicemail/Email' },
  { value: 'redelivery', label: 'Redelivery' },
];

// Generate hour options (1-24)
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i + 1);

export const DeliveryDetailsForm: React.FC<DeliveryDetailsFormProps> = ({
  jobId,
  initialData,
  potentialDeliveryDate,
  deliveryScheduleEmailSentAt,
  deliveryScheduleEmailSentByUsername,
  onSave,
  onStatusChange,
  onEmailSent,
}) => {
  const [startHour, setStartHour] = useState<number | string>(initialData.delivery_window_start || '');
  const [endHour, setEndHour] = useState<number | string>(initialData.delivery_window_end || '');
  const [status, setStatus] = useState<string>(initialData.delivery_status || '');
  const [notes, setNotes] = useState<string>(initialData.delivery_notes || '');
  const [validationError, setValidationError] = useState<string>('');

  useEffect(() => {
    setStartHour(initialData.delivery_window_start || '');
    setEndHour(initialData.delivery_window_end || '');
    setStatus(initialData.delivery_status || '');
    setNotes(initialData.delivery_notes || '');
    setValidationError('');
  }, [
    jobId,
    initialData.delivery_window_start,
    initialData.delivery_window_end,
    initialData.delivery_status,
    initialData.delivery_notes,
  ]);

  const [updateDeliveryDetails, { isLoading }] = useUpdateDeliveryDetailsMutation();
  const [sendDeliveryScheduleEmail, { isLoading: isSendingEmail }] = useSendDeliveryScheduleEmailMutation();
  const toast = useToast();

  const handleSave = async () => {
    // Validation
    setValidationError('');
    
    if (startHour && endHour) {
      const start = Number(startHour);
      const end = Number(endHour);
      
      if (start < 1 || start > 24 || end < 1 || end > 24) {
        setValidationError('Hours must be between 1 and 24');
        return;
      }
      
      if (end <= start) {
        setValidationError('End hour must be greater than start hour');
        return;
      }
    }

    try {
      const data = {
        delivery_window_start: startHour ? Number(startHour) : null,
        delivery_window_end: endHour ? Number(endHour) : null,
        delivery_status: status || null,
        delivery_notes: notes || null,
      };

      await updateDeliveryDetails({
        id: jobId,
        data,
      }).unwrap();

      toast.success('Saved', 'Delivery details updated successfully');
      onSave?.(data);
    } catch (error: any) {
      console.error('Failed to update delivery details:', error);
      toast.error('Save Failed', error?.data?.detail || 'Failed to update delivery details');
    }
  };

  const handleSendEmail = async () => {
    // Validate delivery window is set before sending
    if (!startHour || !endHour) {
      toast.error('Delivery Window Required', 'Please set both start and end times before sending the delivery schedule email');
      return;
    }

    try {
      const result = await sendDeliveryScheduleEmail({
        job_id: jobId,
      }).unwrap();

      if (result.success) {
        toast.success('Email Sent', 'Delivery schedule email has been queued for sending');
        onEmailSent?.();
      } else {
        toast.error('Email Failed', result.error || 'Failed to send delivery schedule email');
      }
    } catch (error: any) {
      console.error('Failed to send delivery schedule email:', error);
      const errorMessage = error?.data?.error || error?.data?.message || 'Failed to send delivery schedule email';
      toast.error('Email Failed', errorMessage);
    }
  };

  const hasChanges = 
    startHour !== (initialData.delivery_window_start || '') ||
    endHour !== (initialData.delivery_window_end || '') ||
    status !== (initialData.delivery_status || '') ||
    notes !== (initialData.delivery_notes || '');

  // Check if delivery window is set (required for sending email)
  const hasDeliveryWindow = Boolean(startHour && endHour);

  return (
    <div className="space-y-3">
      {/* Title */}
      <div className="text-sm font-semibold text-gray-700 border-b border-gray-200 pb-1.5">
        Delivery Window
      </div>

      {/* Time & Status - All in One Row */}
      <div className="grid grid-cols-[1fr_1fr_2fr] gap-2">
        {/* Start Hour */}
        <div>
          <label htmlFor={`start-${jobId}`} className="block text-xs font-medium text-gray-600 mb-1">
            Start
          </label>
          <select
            id={`start-${jobId}`}
            value={startHour}
            onChange={(e) => setStartHour(e.target.value)}
            className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">--</option>
            {HOUR_OPTIONS.map((hour) => (
              <option key={hour} value={hour}>
                {String(hour).padStart(2, '0')}:00
              </option>
            ))}
          </select>
        </div>

        {/* End Hour */}
        <div>
          <label htmlFor={`end-${jobId}`} className="block text-xs font-medium text-gray-600 mb-1">
            End
          </label>
          <select
            id={`end-${jobId}`}
            value={endHour}
            onChange={(e) => setEndHour(e.target.value)}
            className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">--</option>
            {HOUR_OPTIONS.map((hour) => (
              <option key={hour} value={hour}>
                {String(hour).padStart(2, '0')}:00
              </option>
            ))}
          </select>
        </div>

        {/* Status Dropdown */}
        <div>
          <label htmlFor={`status-${jobId}`} className="block text-xs font-medium text-gray-600 mb-1">
            Status
          </label>
          <select
            id={`status-${jobId}`}
            value={status}
            onChange={(e) => {
              const next = e.target.value;
              setStatus(next);
              onStatusChange?.(next);
            }}
            className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Validation Error */}
      {validationError && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
          {validationError}
        </div>
      )}

      {/* Delivery Notes */}
      <div>
        <label htmlFor={`notes-${jobId}`} className="block text-xs font-medium text-gray-600 mb-1">
          Delivery Notes
        </label>
        <textarea
          id={`notes-${jobId}`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Add delivery instructions..."
          className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 resize-none"
        />
      </div>

      {/* Save Button */}
      <Button
        variant="primary"
        size="sm"
        icon={<Save className="w-4 h-4" />}
        onClick={handleSave}
        disabled={!hasChanges || isLoading}
        className="w-full"
      >
        {isLoading ? 'Saving...' : 'Save'}
      </Button>

      {/* Send Delivery Schedule Email Button - Only show when delivery window is set */}
      {hasDeliveryWindow && (
        <div className="pt-2 border-t border-gray-200">
          <Button
            variant={deliveryScheduleEmailSentAt ? "secondary" : "warning"}
            size="sm"
            icon={<Send className="w-4 h-4" />}
            onClick={handleSendEmail}
            disabled={isSendingEmail}
            className="w-full"
          >
            {isSendingEmail ? 'Sending...' : (deliveryScheduleEmailSentAt ? 'Resend Schedule Email' : 'Send Schedule Email')}
          </Button>
          
          {/* Email Sent Status */}
          {deliveryScheduleEmailSentAt && (
            <div className="mt-2 flex items-start gap-2 bg-green-50 border border-green-200 rounded-md p-2">
              <Mail className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-green-800">
                <p className="font-medium">Email Sent</p>
                <p className="text-green-700">
                  by {deliveryScheduleEmailSentByUsername || 'System'} on{' '}
                  {new Date(deliveryScheduleEmailSentAt).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })} at {new Date(deliveryScheduleEmailSentAt).toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

DeliveryDetailsForm.displayName = 'DeliveryDetailsForm';
